import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MultiImageUploader } from "@/components/MultiImageUploader";
import PracticeRunner from "@/components/theory/PracticeRunner";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Play, Video, GripVertical, HelpCircle, Link2 } from "lucide-react";

const DISCIPLINES = ["drone", "helicopter", "aeroplane"];
const LEVELS = ["RPL", "PPL", "CPL"];
const uid = () => crypto.randomUUID();

const TagToggle = ({ options, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`px-2.5 py-1 rounded-full text-xs border capitalize transition-all ${on ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>{o}</button>
      );
    })}
  </div>
);

function QuestionCard({ q, onChange, onDelete }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  const setOpt = (id, k, v) => set("options", q.options.map((o) => (o.id === id ? { ...o, [k]: v } : o)));
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5" data-testid={`question-${q.id}`}>
      <div className="flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-slate-400" />
        <Select value={q.type} onValueChange={(v) => set("type", v)}>
          <SelectTrigger className="h-8 w-40 bg-white" data-testid={`q-type-${q.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="mcq">Multiple choice</SelectItem><SelectItem value="numeric">Numeric (range)</SelectItem></SelectContent>
        </Select>
        <div className="flex-1" />
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onDelete} data-testid={`q-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <Input value={q.prompt} onChange={(e) => set("prompt", e.target.value)} placeholder="Question prompt" className="bg-white" data-testid={`q-prompt-${q.id}`} />

      {q.type === "numeric" ? (
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-[10px] uppercase text-slate-400">Lower (incl.)</Label><Input type="number" step="any" value={q.lower ?? ""} onChange={(e) => set("lower", e.target.value === "" ? null : +e.target.value)} className="bg-white h-9" data-testid={`q-lower-${q.id}`} /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">Upper (incl.)</Label><Input type="number" step="any" value={q.upper ?? ""} onChange={(e) => set("upper", e.target.value === "" ? null : +e.target.value)} className="bg-white h-9" data-testid={`q-upper-${q.id}`} /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">Unit</Label><Input value={q.unit || ""} onChange={(e) => set("unit", e.target.value)} className="bg-white h-9" placeholder="ft, kt…" /></div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-500"><Checkbox checked={q.multi} onCheckedChange={(v) => set("multi", !!v)} /> Allow multiple correct</label>
          </div>
          {(q.options || []).map((o) => (
            <div key={o.id} className="flex items-center gap-2" data-testid={`q-opt-${q.id}-${o.id}`}>
              <Checkbox checked={o.correct} onCheckedChange={(v) => setOpt(o.id, "correct", !!v)} title="Correct answer" data-testid={`q-opt-correct-${o.id}`} />
              <Input value={o.text} onChange={(e) => setOpt(o.id, "text", e.target.value)} placeholder="Option text" className="bg-white h-9 flex-1" />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => set("options", q.options.filter((x) => x.id !== o.id))}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => set("options", [...(q.options || []), { id: uid(), text: "", correct: false }])} data-testid={`q-add-opt-${q.id}`}><Plus className="h-3 w-3 mr-1" /> Add option</Button>
        </div>
      )}
      <Input value={q.explanation || ""} onChange={(e) => set("explanation", e.target.value)} placeholder="Explanation (shown after answering)" className="bg-white text-sm" />
    </div>
  );
}

function ElementCard({ el, idx, onChange, onDelete }) {
  const set = (k, v) => onChange({ ...el, [k]: v });
  const addQ = () => set("questions", [...(el.questions || []), { id: uid(), type: "mcq", prompt: "", options: [{ id: uid(), text: "", correct: true }, { id: uid(), text: "", correct: false }], multi: false, explanation: "" }]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`element-${el.id}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 text-slate-300" />
        <span className="text-xs font-bold text-slate-400">Element {idx + 1}</span>
        <div className="flex-1" />
        <Select value={el.level || "all"} onValueChange={(v) => set("level", v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-32" data-testid={`el-level-${el.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All levels</SelectItem>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l} only</SelectItem>)}</SelectContent>
        </Select>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onDelete} data-testid={`el-delete-${el.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <Input value={el.title} onChange={(e) => set("title", e.target.value)} placeholder="Element title" data-testid={`el-title-${el.id}`} />
      <Textarea value={el.details} onChange={(e) => set("details", e.target.value)} placeholder="Lesson content / details" rows={3} />
      <MultiImageUploader value={el.images || []} onChange={(imgs) => set("images", imgs)} label="Images" testid={`el-images-${el.id}`} />
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Video links (YouTube / Vimeo)</Label>
        <div className="mt-1.5 space-y-1.5">
          {(el.videos || []).map((v, vi) => (
            <div key={vi} className="flex items-center gap-2">
              <Input value={v} onChange={(e) => set("videos", el.videos.map((x, j) => (j === vi ? e.target.value : x)))} placeholder="https://youtube.com/…" className="h-9" data-testid={`el-video-${el.id}-${vi}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => set("videos", el.videos.filter((_, j) => j !== vi))}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => set("videos", [...(el.videos || []), ""])} data-testid={`el-add-video-${el.id}`}><Plus className="h-3 w-3 mr-1" /> Add video</Button>
        </div>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Reference links</Label>
        <div className="mt-1.5 space-y-1.5">
          {(el.reference_links || []).map((v, vi) => (
            <div key={vi} className="flex items-center gap-2">
              <Input value={v} onChange={(e) => set("reference_links", el.reference_links.map((x, j) => (j === vi ? e.target.value : x)))} placeholder="https://www.casa.gov.au/…" className="h-9" data-testid={`el-reflink-${el.id}-${vi}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => set("reference_links", el.reference_links.filter((_, j) => j !== vi))}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => set("reference_links", [...(el.reference_links || []), ""])} data-testid={`el-add-reflink-${el.id}`}><Plus className="h-3 w-3 mr-1" /> Add link</Button>
        </div>
      </div>
      <div className="border-t border-slate-100 pt-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Practice questions</Label>
        <div className="mt-2 space-y-2">
          {(el.questions || []).map((q) => (
            <QuestionCard key={q.id} q={q} onChange={(nq) => set("questions", el.questions.map((x) => (x.id === q.id ? nq : x)))} onDelete={() => set("questions", el.questions.filter((x) => x.id !== q.id))} />
          ))}
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addQ} data-testid={`el-add-question-${el.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add question</Button>
        </div>
      </div>
    </div>
  );
}

export default function LessonBuilder({ lessonId, sectionId, parentId = null, onBack }) {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(!!lessonId);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    if (!lessonId) {
      setLesson({ id: null, section_id: sectionId, parent_id: parentId, title: "", intro: "", disciplines: [], levels: [], elements: [] });
      return;
    }
    try { const r = await api.get(`/theory/lessons/${lessonId}`); setLesson(r.data); }
    catch { toast.error("Failed to load lesson"); } finally { setLoading(false); }
  }, [lessonId, sectionId, parentId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setLesson((l) => ({ ...l, [k]: v }));
  const addElement = () => set("elements", [...(lesson.elements || []), { id: uid(), title: "", level: "", details: "", images: [], videos: [], reference_links: [], questions: [] }]);

  const save = async () => {
    if (!lesson.title.trim()) { toast.error("Lesson needs a title"); return; }
    setSaving(true);
    const payload = { section_id: lesson.section_id, parent_id: lesson.parent_id, title: lesson.title, intro: lesson.intro, disciplines: lesson.disciplines, levels: lesson.levels, elements: lesson.elements, seq: lesson.seq || 0 };
    try {
      const r = lesson.id ? await api.put(`/theory/lessons/${lesson.id}`, payload) : await api.post("/theory/lessons", payload);
      setLesson(r.data);
      toast.success("Lesson saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (loading || !lesson) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-5 fade-up" data-testid="lesson-builder">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="lesson-back"><ArrowLeft className="h-4 w-4" /> Back to course</button>
        <div className="flex items-center gap-2">
          {lesson.id && <Button variant="outline" onClick={() => setPreview(true)} data-testid="lesson-preview-btn"><Play className="h-4 w-4 mr-1.5" /> Preview &amp; practice</Button>}
          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="lesson-save-btn"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save lesson"}</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        {parentId && <p className="text-xs text-lime-600 font-semibold" data-testid="sublesson-flag">Sub-lesson / advanced learning</p>}
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Lesson title</Label>
          <Input value={lesson.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" placeholder="e.g. Airspace Classes" data-testid="lesson-title" /></div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Introduction</Label>
          <Textarea value={lesson.intro} onChange={(e) => set("intro", e.target.value)} className="mt-1.5" rows={2} placeholder="Short intro to this lesson" data-testid="lesson-intro" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Applies to disciplines <span className="normal-case font-normal text-slate-400">(none = all)</span></Label>
            <div className="mt-2"><TagToggle options={DISCIPLINES} value={lesson.disciplines} onChange={(v) => set("disciplines", v)} testid="lesson-disc" /></div></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Applies to levels <span className="normal-case font-normal text-slate-400">(none = all)</span></Label>
            <div className="mt-2"><TagToggle options={LEVELS} value={lesson.levels} onChange={(v) => set("levels", v)} testid="lesson-level" /></div></div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Lesson elements</h3>
        {(lesson.elements || []).map((el, i) => (
          <ElementCard key={el.id} el={el} idx={i} onChange={(ne) => set("elements", lesson.elements.map((x) => (x.id === el.id ? ne : x)))} onDelete={() => set("elements", lesson.elements.filter((x) => x.id !== el.id))} />
        ))}
        <Button variant="outline" onClick={addElement} data-testid="add-element-btn"><Plus className="h-4 w-4 mr-1.5" /> Add element</Button>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lesson-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading">{lesson.title} — practice preview</DialogTitle>
            <DialogDescription>Answer the practice questions and submit to see your score.</DialogDescription></DialogHeader>
          <PracticeRunner lesson={lesson} traineeId="preview" onClose={() => setPreview(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
